import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

// @ts-expect-error TS2724
import type { ProviderPackageItemModel, ProviderPackageItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerPackageItems']

type ProviderPackageItemFilters = {
  provider_package_id?: number,
  string?: string,
}

type UseProviderPackageItemsOptions = {
  filters?: ProviderPackageItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderPackageItemRequestOptions,
}

function useProviderPackageItems(options: UseProviderPackageItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_package_id: filterProviderPackageId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { providerPackageItems: providerPackageItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerPackageItems } = useSelector(reduxState => reduxState.entities)

  const filteredProviderPackageItems = useMemo(() => {
    const filtered: ProviderPackageItemModel[] = Object.values(providerPackageItems)
      // @ts-expect-error TS2769
      .filter((providerPackageItem: ProviderPackageItemModel) => {
        const { id, provider_package_id, title } = providerPackageItem

        const matchId = id.toString() === filterString
        const matchTitle = matchFilterString(title, filterString)
        const matchString = matchId || matchTitle

        const matchProviderPackageId = matchFilterNumber(provider_package_id, filterProviderPackageId)

        return matchProviderPackageId && matchString
      })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [providerPackageItemsUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPackageItemsCount = filteredProviderPackageItems.length
  const hasFilteredProviderPackageItems = !!filteredProviderPackageItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderPackageItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPackageItems } = useReduxAction(
    'providerPackageItems',
    'loadProviderPackageItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPackageItems,
    filteredProviderPackageItemsCount,
    hasFilteredProviderPackageItems,
    loading: loadingProviderPackageItems,
  }
}

export default useProviderPackageItems
